import React, { FC, useContext, useMemo } from 'react';
import { UserLicensesFetcher } from '../fetchers/UserLicensesFetcher';
import { LicensesContext } from './LicensesContext';
import { CustomerProductLicensesFetcher } from '../fetchers/CustomerProductLicensesFetcher';
import { OrganizationAllLicensesFetcher } from '../fetchers/OrganizationAllLicensesFetcher';
import { OrganizationAllLicensesContext } from './OrganizationAllLicensesContext';

interface IProps {
  hideDiscovery?: boolean;
}

/**
 * @description This context provider is an adapter that makes data from UserLicensesFetcher accessible via LicensesContext
 */
export const LicensesContextFromUserLicenses: FC<IProps> = ({ children, hideDiscovery }) => {
  const { data, onRefresh } = useContext(UserLicensesFetcher.Context);

  const val = useMemo(
    () => ({
      licenses: hideDiscovery ? data?.filter(({ isDiscovery }) => !isDiscovery) ?? null : data,
      onRefresh,
    }),
    [data, onRefresh, hideDiscovery]
  );

  return <LicensesContext.Provider value={val}>{children}</LicensesContext.Provider>;
};

/**
 * @description This context provider is an adapter that makes data from CustomerProductLicensesFetcher accessible via LicensesContext
 */
export const LicensesContextFromCustomerProductLicenses: FC = ({ children }) => {
  const { data, onRefresh } = useContext(CustomerProductLicensesFetcher.Context);

  const val = useMemo(
    () => ({
      licenses: data,
      onRefresh,
    }),
    [data, onRefresh]
  );

  return <LicensesContext.Provider value={val}>{children}</LicensesContext.Provider>;
};

/**
 * @description Adapter that provides All Licenses batch API data via OrganizationAllLicensesContext
 */
export const OrganizationAllLicensesContextFromFetcher: FC<IProps> = ({ children, hideDiscovery }) => {
  const { data, onRefresh } = useContext(OrganizationAllLicensesFetcher.Context);

  const val = useMemo(
    () => ({
      licenses: hideDiscovery ? data?.filter(({ isDiscovery }) => !isDiscovery) ?? null : data,
      onRefresh,
    }),
    [data, onRefresh, hideDiscovery]
  );

  return <OrganizationAllLicensesContext.Provider value={val}>{children}</OrganizationAllLicensesContext.Provider>;
};
